import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { Dialog } from "../Dialog";
import MetadataEditor from "../Metadata/MetadataEditor";
import { saveItemMetadata } from "../../services/data";
import { metadataText, normalizeMetadataErrors, parseMetadataText } from "../../utils/datasetMetadata";
import { inferSchema } from "../../utils/schemaInference";

// Edit a data item's metadata, and optionally export it as the dataset's schema.
//
// The metadata is validated server-side against `metadata_schema`; the export derives a schema
// from this item's actual metadata so a dataset can be given a schema that matches real data.

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12.5px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--main-border-color);
  border-radius: 8px;
  background-color: var(--main-bg);
  color: var(--main-font-color);
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--main-font-color);
`;

const Code = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: 8px 10px;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.5;
  color: var(--main-font-color);
  background-color: var(--content-item-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  resize: vertical;
  box-sizing: border-box;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #a0a6b1;
`;

const Notice = styled.div`
  font-size: 12.5px;
  color: #2ecc71;
`;

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {object} props.item
 * @param {Function} [props.onClose]
 * @param {(publicId: string, metadata: object) => void} [props.onSaved]
 * @param {(schema: object) => void} [props.onUseAsSchema] Receives the inferred schema, e.g. to
 *   set it on the dataset (persisted with the dataset's Save Changes).
 */
export const ItemMetadataDialog = ({
  show,
  item,
  onClose = () => {},
  onSaved = () => {},
  onUseAsSchema = null,
}) => {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState([]);

  const [schema, setSchema] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState(null);

  // Reset the draft each time the dialog opens; the item object stays stable while it is open.
  useEffect(() => {
    if (show) {
      setDraft(metadataText(item?.metadata));
      setDirty(false);
      setSaving(false);
      setMessage(null);
      setErrors([]);
      setSchema(null);
      setCopied(false);
      setNotice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const save = async () => {
    if (saving) return;

    // `PUT /admin/dataset/item/<id>` takes the dataset-item public id; a row may expose it on the
    // item itself or under `data_set_item`.
    const itemPublicId = item?.data_set_item?.public_id ?? item?.item_public_id ?? item?.public_id;

    if (!itemPublicId) {
      setMessage("This item has no id to save against.");
      return;
    }

    const parsed = parseMetadataText(draft);
    if (!parsed.ok) {
      setMessage(`Metadata is not valid JSON: ${parsed.message}`);
      return;
    }

    setSaving(true);
    setMessage(null);
    setErrors([]);

    const results = await saveItemMetadata(itemPublicId, parsed.value);

    setSaving(false);

    if (!results?.success) {
      setMessage(results?.message || "Failed to save the metadata.");
      setErrors(normalizeMetadataErrors(results));
      return;
    }

    onSaved(item.public_id ?? itemPublicId, parsed.value);
    setDirty(false);
    onClose();
  };

  const exportSchema = () => {
    setNotice(null);
    setCopied(false);

    const parsed = parseMetadataText(draft);
    if (!parsed.ok) {
      setMessage(`Metadata is not valid JSON: ${parsed.message}`);
      return;
    }

    if (parsed.value === null) {
      setMessage("This item has no metadata to derive a schema from.");
      return;
    }

    setMessage(null);
    setSchema(inferSchema(parsed.value));
  };

  const copySchema = async () => {
    if (!schema) return;

    try {
      await navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
      setCopied(true);
    } catch (error) {
      console.log("CLIPBOARD ERROR: ", error);
    }
  };

  return (
    <Dialog
      title="Data Item Metadata"
      subtitle={item?.name ?? item?.short_title ?? ""}
      show={show}
      onClose={onClose}
      onOK={save}
      okText={saving ? "Saving..." : "Save"}
      closeText="Close"
      style={{ width: "90%", maxWidth: "1000px", height: "80vh" }}
    >
      <Toolbar>
        <div style={{ fontSize: "13px", color: "#a0a6b1" }}>
          Add, change or remove keys, then Save. Changes are not stored until you save.
          {dirty ? " (unsaved)" : ""}
        </div>
        <span style={{ flex: 1 }} />
        <Small type="button" onClick={exportSchema} title="Derive a JSON Schema from this metadata">
          <Icon icon="lucide:braces" /> Export schema
        </Small>
      </Toolbar>

      {message ? <div style={{ fontSize: "13px", color: "#e74c3c" }}>{message}</div> : null}

      {errors.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12.5px", color: "#e74c3c" }}>
          {errors.map((error, index) => (
            <li key={`${error.path}-${index}`}>
              <code>{error.path}</code>: {error.message}
            </li>
          ))}
        </ul>
      ) : null}

      {schema ? (
        <Panel>
          <PanelTitle>
            <Icon icon="lucide:braces" /> Inferred schema
          </PanelTitle>
          <Hint>
            Generated from this item's metadata: observed keys are required and types follow the
            current values. Review it before using it as the dataset schema.
          </Hint>
          <Code readOnly spellCheck={false} value={JSON.stringify(schema, null, 2)} />
          <Toolbar>
            <Small type="button" onClick={copySchema}>
              <Icon icon={copied ? "lucide:check" : "lucide:copy"} /> {copied ? "Copied" : "Copy JSON"}
            </Small>
            {onUseAsSchema ? (
              <Small
                type="button"
                onClick={() => {
                  onUseAsSchema(schema);
                  setNotice(
                    "Set as the dataset schema — open the dataset's Metadata tab and Save Changes to persist it."
                  );
                }}
              >
                <Icon icon="lucide:file-json" /> Use as dataset schema
              </Small>
            ) : null}
            <Small type="button" onClick={() => setSchema(null)}>
              <Icon icon="lucide:x" /> Close preview
            </Small>
          </Toolbar>
          {notice ? <Notice>{notice}</Notice> : null}
        </Panel>
      ) : null}

      <MetadataEditor
        value={draft}
        onChange={(next) => {
          setDraft(next);
          setDirty(true);
          setMessage(null);
          setErrors([]);
        }}
      />
    </Dialog>
  );
};

export default ItemMetadataDialog;
